import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Table, Spin, Avatar, Button, Tag, Radio, Space, DatePicker } from 'antd'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import Layout from '../../components/layout/Layout'
import axiosInstance from '../../utils/axiosInstance'

const { RangePicker } = DatePicker

function LikePostLogs() {
  const navigate = useNavigate()

  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const isFetchingRef = useRef(false)
  const [filter, setFilter] = useState('all')
  const [dateRange, setDateRange] = useState(null)

  const fetchLogs = async (pageNumber = 1) => {
    if (isFetchingRef.current) return

    setLoading(true)
    isFetchingRef.current = true

    try {
      const response = await axiosInstance.get(`/api/v1/logs/getScreenVisits?screenName=likePost&page=${pageNumber}`)
      let newLogs = response.data.logs || []

      setLogs((prev) => [...prev, ...newLogs])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
      isFetchingRef.current = false
    }
  }

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <div>
              {record.userDetails?.firstName} {record.userDetails?.lastName}
            </div>
            <small>{record.userDetails?.email}</small>
          </div>
        </div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (v) => {
        return v === 'like' ? <Tag color="green">Like</Tag> : <Tag color="red">Unlike</Tag>
      },
    },
    {
      title: 'Post Owner',
      key: 'postOwner',
      render: (_, record) => (
        <div>
          <div>
            {record.postOwnerDetails?.firstName} {record.postOwnerDetails?.lastName}
          </div>
          <small>{record.postOwnerDetails?.email}</small>
        </div>
      ),
    },
    {
      title: 'Post Message',
      key: 'postMessage',
      render: (_, record) => {
        return record.postDetails?._id ? (
          <p style={{ maxWidth: 200 }}>
            <a href={`/anEventDetails/${record.postDetails?._id}`} target="_blank">
              Open
            </a>
          </p>
        ) : (
          <p>-</p>
        )
      },
    },
    {
      title: 'Execution time',
      key: 'approximateExecutionTime',
      render: (record) => {
        if (record.approximateExecutionTime) {
          const endOne = record.approximateExecutionTime[0]
          const endTwo = record.approximateExecutionTime[1] / 1e6
          return (
            <small>
              <span style={{ color: 'red' }}>{endOne > 0 ? `${endOne}s, ` : ``}</span>
              {endTwo.toFixed(2)}ms ({record.currentOnline})
            </small>
          )
        } else {
          return <p>-</p>
        }
      },
    },
    {
      title: 'Visited At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => <small>{moment(v).format('h:mm:ss A, DD MMM YYYY')}</small>,
    },
  ]

  const filteredLogs = useMemo(() => {
    let result = [...logs]

    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange
      result = result.filter((log) => log.createdAt >= start && log.createdAt <= end)
    }

    if (filter === 'like') {
      result = result.filter((log) => log.action === 'like')
    } else if (filter === 'unlike') {
      result = result.filter((log) => log.action === 'unlike')
    }

    return result
  }, [logs, filter, dateRange])

  useEffect(() => {
    fetchLogs(page)
  }, [page])

  return (
    <Layout>
      <div style={{ padding: 20 }}>
        <h2>Like Post Logs</h2>

        <Space>
          <Button type="primary" onClick={() => navigate(-1)} style={{ marginBottom: 0 }}>
            Back
          </Button>

          <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)}>
            <Radio.Button value="all">All</Radio.Button>
            <Radio.Button value="like">Like</Radio.Button>
            <Radio.Button value="unlike">Unlike</Radio.Button>
          </Radio.Group>

          <RangePicker
            showTime
            onChange={(dates) => {
              if (!dates) {
                setDateRange(null)
              } else {
                setDateRange([dates[0].valueOf(), dates[1].valueOf()])
              }
            }}
          />

          {dateRange && <Button onClick={() => setDateRange(null)}>Clear Date</Button>}
        </Space>

        <p>Total items at table: {filteredLogs.length}</p>

        <Table rowKey="_id" columns={columns} dataSource={filteredLogs} pagination={false} scroll={{ x: 600, y: 500 }} />

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          {loading ? (
            <Spin />
          ) : (
            <Button type="primary" onClick={() => setPage((prev) => prev + 1)}>
              Load More
            </Button>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default LikePostLogs
